import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface OAuthDiagnostic {
  platform: string;
  status: 'working' | 'error' | 'not_configured';
  message: string;
  redirectUri?: string;
  error?: string;
}

export async function diagnoseOAuthPlatform(platform: string): Promise<OAuthDiagnostic> {
  try {
    console.log(`🔍 Diagnosing OAuth for platform: ${platform}`);
    
    // Test if the oauth-initiate function exists and responds
    const { data, error } = await supabase.functions.invoke('oauth-initiate', {
      body: {
        platform,
        user_id: 'test-user-id',
        test_mode: true // Add test mode to avoid creating actual OAuth flows
      }
    });

    console.log(`📊 OAuth diagnostic result for ${platform}:`, { data, error });

    if (error) {
      return {
        platform,
        status: 'error',
        message: `OAuth initiation failed: ${error.message}`,
        error: error.message
      };
    }

    if (data && data.authUrl) {
      return {
        platform,
        status: 'working',
        message: 'OAuth configuration is working correctly',
        redirectUri: data.authUrl
      };
    }

    return {
      platform,
      status: 'not_configured',
      message: 'OAuth configuration missing or incomplete'
    };

  } catch (error: any) {
    console.error(`❌ Error diagnosing ${platform}:`, error);
    return {
      platform,
      status: 'error',
      message: `Diagnostic failed: ${error.message}`,
      error: error.message
    };
  }
}

export async function runOAuthDiagnostics(): Promise<OAuthDiagnostic[]> {
  const platforms = ['reddit', 'youtube'];

  const results: OAuthDiagnostic[] = [];
  
  toast.info('Running OAuth diagnostics...', {
    description: 'Testing all platform connections'
  });

  for (const platform of platforms) {
    const result = await diagnoseOAuthPlatform(platform);
    results.push(result);
  }

  // Summarize results
  const working = results.filter(r => r.status === 'working').length;
  const errors = results.filter(r => r.status === 'error').length;
  const notConfigured = results.filter(r => r.status === 'not_configured').length;

  console.log('🔍 OAuth Diagnostics Summary:', {
    total: results.length,
    working,
    errors,
    notConfigured,
    results
  });

  if (errors > 0) {
    toast.error(`OAuth Issues Found`, {
      description: `${errors} platforms have errors, ${working} working correctly`
    });
  } else {
    toast.success(`OAuth Diagnostics Complete`, {
      description: `${working} platforms working correctly`
    });
  }

  return results;
}